/**
 * useChatbot Hook
 *
 * Manages chatbot panel open/close state, current session, message list,
 * and streaming state. Orchestrates sending messages via SSE.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { sendMessage, getSessionMessages, createSession, listSessions } from '../services/chatbotApi';

import { readToken } from '../../../shared/auth/session';

/**
 * @returns {Object} chatbot state and actions
 */
export function useChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamError, setStreamError] = useState(null);
  const [contextUsage, setContextUsage] = useState(null);
  const [rolloverInfo, setRolloverInfo] = useState(null);
  const [thresholdWarningShown, setThresholdWarningShown] = useState(false);
  const [followupSuggestions, setFollowupSuggestions] = useState([]);
  const abortRef = useRef(null);

  /** Toggle panel open/close */
  const togglePanel = useCallback(() => setIsOpen((prev) => !prev), []);
  const openPanel = useCallback(() => setIsOpen(true), []);
  const closePanel = useCallback(() => setIsOpen(false), []);

  /** Load messages for a session (full replace — used for session switching, initial load) */
  const loadMessages = useCallback(async (sessionId) => {
    try {
      const data = await getSessionMessages(sessionId);
      const msgs = Array.isArray(data) ? data : data?.messages || [];
      setMessages(msgs);
      setCurrentSessionId(sessionId);
      setRolloverInfo(null);
      setThresholdWarningShown(false);
      // Reset context usage — the real values come from the backend
      // via the metadata SSE event when the user sends a message.
      // Don't guess summary_active from message count — that causes
      // the "Conversation above has been summarized" banner to show
      // incorrectly on sessions that haven't actually been summarized.
      const count = msgs.length;
      if (count > 0) {
        const fullness = Math.min((count / 80) * 100, 100);
        setContextUsage({
          total_messages: count,
          window_max: 30,
          summary_active: false,
          context_fullness_pct: fullness,
          threshold_warning: false,
          messages_in_window: Math.min(count, 30),
        });
      } else {
        setContextUsage(null);
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  }, []);

  /**
   * Silently sync temp message IDs with server-persisted IDs.
   * Keeps the existing content in place (no scroll jump) — only patches IDs.
   */
  const syncMessageIds = useCallback(async (sessionId) => {
    try {
      const data = await getSessionMessages(sessionId);
      const serverMsgs = Array.isArray(data) ? data : data?.messages || [];
      if (serverMsgs.length === 0) return;

      setMessages((prev) => {
        // Build a map of server messages by role+content for matching
        const serverQueue = [...serverMsgs];
        return prev.map((local) => {
          if (!local.message_id?.startsWith('temp')) return local;
          // Find matching server message (same role, same content)
          const matchIdx = serverQueue.findIndex(
            (s) => s.role === local.role && s.content === local.content
          );
          if (matchIdx >= 0) {
            const match = serverQueue.splice(matchIdx, 1)[0];
            return { ...local, message_id: match.message_id, timestamp: match.timestamp };
          }
          return local;
        });
      });

      // Update context usage from the real count
      const count = serverMsgs.length;
      if (count > 0) {
        const fullness = Math.min((count / 80) * 100, 100);
        setContextUsage({
          total_messages: count,
          window_max: 30,
          summary_active: count > 30,
          context_fullness_pct: fullness,
          threshold_warning: fullness >= 80,
          messages_in_window: Math.min(count, 30),
        });
      }
    } catch (err) {
      // Non-critical — temp IDs still work for display
      console.warn('Failed to sync message IDs:', err);
    }
  }, []);

  /** Switch to a session (load its messages) */
  const switchSession = useCallback(
    async (sessionId) => {
      if (sessionId === currentSessionId) return;
      await loadMessages(sessionId);
    },
    [currentSessionId, loadMessages],
  );

  /** Start a new chat session */
  const startNewChat = useCallback(async () => {
    try {
      const session = await createSession();
      const id = session?.session_id || session?.sessionId;
      setCurrentSessionId(id);
      setMessages([]);
      setContextUsage(null);
      setRolloverInfo(null);
      setThresholdWarningShown(false);
      return id;
    } catch (err) {
      console.error('Failed to create session:', err);
      return null;
    }
  }, []);

  /** Send a message and stream the response */
  const send = useCallback(
    async (content, context) => {
      if (!content.trim() || isStreaming) return;

      let sessionId = currentSessionId;
      if (!sessionId) {
        sessionId = await startNewChat();
        if (!sessionId) return;
      }

      // Optimistically add user message
      const userMsg = {
        message_id: `temp-${Date.now()}`,
        role: 'user',
        content,
        timestamp: new Date().toISOString(),
        feedback: null,
      };
      setMessages((prev) => [...prev, userMsg]);

      // Prepare assistant placeholder
      const assistantMsg = {
        message_id: `temp-assistant-${Date.now()}`,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        feedback: null,
      };
      setMessages((prev) => [...prev, assistantMsg]);

      setIsStreaming(true);
      setStreamError(null);
      setFollowupSuggestions([]);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        await sendMessage({
          sessionId,
          content,
          context,
          signal: controller.signal,
          onToken: (token) => {
            setMessages((prev) => {
              const updated = [...prev];
              const last = updated[updated.length - 1];
              if (last && last.role === 'assistant') {
                updated[updated.length - 1] = { ...last, content: last.content + token };
              }
              return updated;
            });
          },
          onMetadata: (metadata) => {
            if (metadata?.context_usage) {
              setContextUsage(metadata.context_usage);
              // Show threshold warning when hitting 80%
              if (metadata.context_usage.threshold_warning && !thresholdWarningShown) {
                setThresholdWarningShown(true);
              }
            }
          },
          onRollover: (data) => {
            setRolloverInfo(data);
          },
          onFollowups: (data) => {
            if (data?.suggestions) {
              setFollowupSuggestions(data.suggestions);
            }
          },
          onError: (errData) => {
            setStreamError(errData);
          },
          onDone: () => {
            // Silently sync temp IDs with server-persisted IDs (no scroll jump)
            syncMessageIds(sessionId);
          },
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setStreamError({ message: err.message, retry: true });
        }
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [currentSessionId, isStreaming, startNewChat, syncMessageIds],
  );

  /** Cancel an in-progress stream */
  const cancelStream = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
  }, []);

  /** Retry the last failed message */
  const retry = useCallback(() => {
    if (messages.length < 2) return;
    // Find the last user message
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
    if (!lastUserMsg) return;
    // Remove the failed assistant message
    setMessages((prev) => prev.filter((m) => m.role !== 'assistant' || m.content));
    setStreamError(null);
    send(lastUserMsg.content);
  }, [messages, send]);

  // Keyboard shortcut: Cmd+J / Ctrl+J
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'j') {
        e.preventDefault();
        togglePanel();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [togglePanel]);

  // Auto-load the most recent session on mount so returning users
  // see their latest conversation immediately.
  //
  // Guarded on authentication: this must not run before the user has signed in.
  // useChatbot() is instantiated in AppContent, which also renders the landing/
  // sign-in pages, so without this guard the effect fired on the very first
  // (pre-auth) mount and sent an unauthenticated GET /chat/sessions that 401'd.
  // `hasToken` is recomputed on every render, so once a token appears after
  // login the dependency flips false -> true and the auto-load runs exactly once.
  const initialLoadDone = useRef(false);
  const hasToken = !!readToken();
  useEffect(() => {
    if (initialLoadDone.current) return;
    if (!hasToken) return; // wait for auth; don't consume the one-shot ref yet
    initialLoadDone.current = true;

    (async () => {
      try {
        const data = await listSessions();
        const sessions = Array.isArray(data) ? data : data?.sessions || [];
        if (sessions.length > 0) {
          const latest = sessions[0]; // sorted by updated_at DESC from backend
          const id = latest.session_id || latest.sessionId;
          await loadMessages(id);
        }
      } catch (err) {
        // Silently fail — user just won't have a pre-loaded session
        console.warn('Auto-load latest session failed:', err);
      }
    })();
  }, [hasToken, loadMessages]);

  /** Edit a user message and resend — truncates messages after the edited one */
  const editAndResend = useCallback(
    async (messageId, newContent, context) => {
      if (!newContent.trim() || isStreaming) return;

      // Find the edited message index
      const idx = messages.findIndex((m) => m.message_id === messageId);
      if (idx < 0) return;

      // Truncate: keep messages up to (but not including) the edited one
      const kept = messages.slice(0, idx);
      setMessages(kept);

      // Now send the new content as a fresh message (reuses the existing send flow)
      await send(newContent, context);
    },
    [messages, isStreaming, send],
  );

  return {
    isOpen,
    togglePanel,
    openPanel,
    closePanel,
    currentSessionId,
    messages,
    isStreaming,
    streamError,
    contextUsage,
    rolloverInfo,
    thresholdWarningShown,
    setThresholdWarningShown,
    send,
    cancelStream,
    retry,
    switchSession,
    startNewChat,
    loadMessages,
    setCurrentSessionId,
    editAndResend,
    followupSuggestions,
  };
}

export default useChatbot;
